import React, { useState, useEffect } from 'react';
import {
  Shield,
  Check,
  X,
  User,
  Users,
  Lock,
  Layers,
  Save,
  CheckCircle2,
  AlertCircle,
  KeyRound,
  Eye,
  EyeOff,
  Sliders,
  DollarSign,
  FileText,
  Truck,
  Building2,
  Trash2,
  Download
} from 'lucide-react';
import {
  UserPermissionProfile,
  GroupWithPrograms,
  SystemProgram,
  userManagementService,
  CONTROLLER_TO_PERMISSIONS
} from '../services/userManagementService';
import { AppPermission, AppRole, PERMISSION_DEFINITIONS } from '../types/permissions';
import { ROLE_PERMISSIONS, ALL_PERMISSIONS } from '../services/permissionsService';
import { usePermissionsContext } from '../contexts/PermissionsContext';

interface Props {
  user: UserPermissionProfile | null;
  isOpen: boolean;
  onClose: () => void;
  availableGroups: GroupWithPrograms[];
  allPrograms: SystemProgram[];
  onSaved: () => void;
}

export const EditarPermissoesModal: React.FC<Props> = ({
  user,
  isOpen,
  onClose,
  availableGroups,
  allPrograms,
  onSaved
}) => {
  const { reloadPermissions } = usePermissionsContext();
  const [role, setRole] = useState<AppRole>(user?.role || 'user');
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>(user?.groups?.map(g => g.id) || []);
  const [selectedDirectProgramIds, setSelectedDirectProgramIds] = useState<number[]>(
    user?.direct_programs?.map(p => p.id) || []
  );
  const [granularPermissions, setGranularPermissions] = useState<AppPermission[]>(
    user?.custom_permissions !== undefined && Array.isArray(user.custom_permissions)
      ? user.custom_permissions
      : (user?.role ? (ROLE_PERMISSIONS[user.role] || ROLE_PERMISSIONS.user) : ROLE_PERMISSIONS.user)
  );
  const [isActive, setIsActive] = useState<boolean>(user?.active === 'Y' || user?.active === 'S');
  // Padrão: segue o(s) grupo(s) do usuário. Só vira "customizado" se ele já
  // tinha uma lista granular fixada antes, ou se o admin ligar manualmente.
  const [usarPermissoesCustomizadas, setUsarPermissoesCustomizadas] = useState<boolean>(
    !!user?.permissoes_customizadas
  );
  const [salvando, setSalvando] = useState<boolean>(false);
  const [sucesso, setSucesso] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<'grupos' | 'programas_diretos' | 'granular' | 'visao_geral'>('grupos');
  const [filterCategory, setFilterCategory] = useState<string>('todas');
  const [searchPerm, setSearchPerm] = useState<string>('');

  useEffect(() => {
    if (user) {
      setRole(user.role || 'user');
      setSelectedGroupIds(user.groups?.map(g => g.id) || []);
      setSelectedDirectProgramIds(user.direct_programs?.map(p => p.id) || []);
      setGranularPermissions(
        user.custom_permissions !== undefined && Array.isArray(user.custom_permissions)
          ? user.custom_permissions
          : (ROLE_PERMISSIONS[user.role] || ROLE_PERMISSIONS.user)
      );
      setIsActive(user.active === 'Y' || user.active === 'S');
      setUsarPermissoesCustomizadas(!!user.permissoes_customizadas);
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleToggleGroup = (groupId: number) => {
    if (selectedGroupIds.includes(groupId)) {
      setSelectedGroupIds(selectedGroupIds.filter(id => id !== groupId));
    } else {
      setSelectedGroupIds([...selectedGroupIds, groupId]);
    }
  };

  const handleToggleDirectProgram = (programId: number) => {
    if (selectedDirectProgramIds.includes(programId)) {
      setSelectedDirectProgramIds(selectedDirectProgramIds.filter(id => id !== programId));
    } else {
      setSelectedDirectProgramIds([...selectedDirectProgramIds, programId]);
    }
  };

  const handleToggleGranularPermission = (permId: AppPermission) => {
    if (!usarPermissoesCustomizadas) return; // somente leitura enquanto segue o grupo
    if (granularPermissions.includes(permId)) {
      setGranularPermissions(granularPermissions.filter(p => p !== permId));
    } else {
      setGranularPermissions([...granularPermissions, permId]);
    }
  };

  // Liga/desliga o modo customizado. Ao ligar, parte do que o grupo já
  // concede hoje (ponto de partida mais previsível do que uma lista antiga
  // ou vazia). Ao desligar, o usuário volta a seguir o grupo automaticamente.
  const handleToggleModoCustomizado = () => {
    setUsarPermissoesCustomizadas((prev) => {
      const proximo = !prev;
      if (proximo) setGranularPermissions(permissoesDoGrupo);
      return proximo;
    });
  };

  const handleSelectAllPerms = () => {
    setUsarPermissoesCustomizadas(true); // qualquer edição manual liga o modo customizado
    setGranularPermissions(ALL_PERMISSIONS);
  };

  const handleClearSensitivePerms = () => {
    setUsarPermissoesCustomizadas(true);
    // Remove permissões de valores, repasses, margem e exclusão
    const sensitiveList: AppPermission[] = [
      'view_order_repasse',
      'edit_order_repasse',
      'view_order_margin',
      'view_financial_values',
      'view_financial_dre',
      'delete_orders',
      'delete_financial',
      'delete_people',
      'delete_vehicles',
      'delete_user',
      'view_people_docs',
      'view_people_bank',
      'manage_bank_gateways'
    ];
    // Parte do que está exibido agora (grupo ou customizada), não de uma
    // lista customizada antiga que talvez nunca tenha sido usada de verdade.
    setGranularPermissions(permissoesExibidas.filter(p => !sensitiveList.includes(p)));
  };

  // Calcula os programas que o usuário tem acesso (herança de grupo + programas diretos)
  const inheritedProgramIds = new Set<number>();
  selectedGroupIds.forEach(gid => {
    const grp = availableGroups.find(g => g.id === gid);
    if (grp) {
      grp.program_ids.forEach(pid => inheritedProgramIds.add(pid));
    }
  });

  const allEffectiveProgramIds = new Set([...Array.from(inheritedProgramIds), ...selectedDirectProgramIds]);
  if (role === 'admin' || selectedGroupIds.includes(1)) {
    allPrograms.forEach(p => allEffectiveProgramIds.add(p.id));
  }

  const effectivePrograms = Array.from(allEffectiveProgramIds)
    .map(pid => allPrograms.find(p => p.id === pid))
    .filter(Boolean) as SystemProgram[];

  // Permissões que o(s) grupo(s) + programas diretos concedem hoje - é o que
  // vale para este usuário quando "usarPermissoesCustomizadas" está desligado.
  // Mesma lógica de userManagementService.getUsuariosComPermissoes().
  const permissoesDoGrupoSet = new Set<AppPermission>();
  effectivePrograms.forEach(prog => {
    const mapped = CONTROLLER_TO_PERMISSIONS[prog.controller];
    if (mapped) mapped.forEach(p => permissoesDoGrupoSet.add(p));
  });
  if (role === 'admin' || selectedGroupIds.includes(1)) {
    ALL_PERMISSIONS.forEach(p => permissoesDoGrupoSet.add(p));
  }
  const permissoesDoGrupo = Array.from(permissoesDoGrupoSet);

  // O que fica marcado na lista: a customizada (se ligada) ou a do grupo.
  const permissoesExibidas = usarPermissoesCustomizadas ? granularPermissions : permissoesDoGrupo;

  const handleSalvar = async () => {
    setSalvando(true);
    try {
      const ok = await userManagementService.salvarPermissoesUsuario(
        user.id,
        role,
        granularPermissions,
        selectedGroupIds,
        selectedDirectProgramIds,
        isActive ? 'Y' : 'N',
        usarPermissoesCustomizadas
      );

      if (ok) {
        setSucesso(true);
        try {
          await reloadPermissions();
        } catch (e) {
          // Silent reload attempt
        }
        setTimeout(() => {
          setSucesso(false);
          onSaved();
          onClose();
        }, 1200);
      }
    } catch (e) {
      console.error('Erro ao salvar permissões relacionais:', e);
    } finally {
      setSalvando(false);
    }
  };

  // Agrupamento de permissões por categoria
  const categories = Array.from(new Set(PERMISSION_DEFINITIONS.map(p => p.category)));

  const filteredPermDefinitions = PERMISSION_DEFINITIONS.filter(p => {
    const matchesCat = filterCategory === 'todas' || p.category === filterCategory;
    const matchesSearch = !searchPerm || 
      p.label.toLowerCase().includes(searchPerm.toLowerCase()) || 
      p.description.toLowerCase().includes(searchPerm.toLowerCase()) ||
      p.id.toLowerCase().includes(searchPerm.toLowerCase());
    return matchesCat && matchesSearch;
  });

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-2 sm:p-4 md:p-6 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-700/80 rounded-2xl sm:rounded-3xl w-full max-w-4xl max-h-[94vh] flex flex-col shadow-2xl overflow-hidden text-left my-auto">
        
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-slate-800 flex items-start sm:items-center justify-between gap-3 bg-slate-950/60 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-2xl bg-indigo-600/20 text-indigo-400 flex items-center justify-center border border-indigo-500/30 shrink-0">
              <Shield className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div className="min-w-0">
              <h2 className="text-sm sm:text-base md:text-lg font-black text-white flex flex-wrap items-center gap-2">
                <span className="truncate">Gerenciador de Permissões & Bloqueio</span>
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-indigo-950 text-indigo-300 border border-indigo-800 shrink-0">
                  #{user.id} {user.login}
                </span>
              </h2>
              <p className="text-xs text-slate-400 truncate sm:whitespace-normal">
                Configure grupos, telas e bloqueio de campos mínimos e valores confidenciais para <strong className="text-slate-200">{user.name}</strong>.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition shrink-0 cursor-pointer"
            title="Fechar modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Notificação de Sucesso */}
        {sucesso && (
          <div className="mx-4 sm:mx-6 mt-3 sm:mt-4 p-3 rounded-2xl bg-emerald-950/80 border border-emerald-800 text-emerald-300 text-xs font-bold flex items-center gap-2 shrink-0 animate-in fade-in">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>Permissões, grupos e bloqueios granulares salvos com sucesso no Supabase!</span>
          </div>
        )}

        {/* Informações Supabase Auth & Role */}
        <div className="px-4 sm:px-6 pt-3 sm:pt-4 shrink-0">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2.5 sm:gap-3 p-3 sm:p-3.5 rounded-2xl bg-slate-950/70 border border-slate-800 text-xs">
            <div className="min-w-0">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Vínculo Supabase Auth</div>
              <div className="text-white font-mono mt-0.5 truncate flex items-center gap-1.5">
                {user.auth_user_id ? (
                  <span className="text-emerald-400 flex items-center gap-1 truncate" title={user.auth_user_id}>
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> UUID: {user.auth_user_id.substring(0, 12)}...
                  </span>
                ) : (
                  <span className="text-amber-400 flex items-center gap-1 truncate" title={user.email || user.login}>
                    <AlertCircle className="w-3.5 h-3.5 shrink-0" /> Email ({user.email || user.login})
                  </span>
                )}
              </div>
            </div>

            <div className="min-w-0">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Perfil Base (Role)</div>
              <select
                value={role}
                onChange={(e) => {
                  const newRole = e.target.value as AppRole;
                  setRole(newRole);
                  if (newRole === 'admin') {
                    setSelectedGroupIds([1]);
                    setGranularPermissions(ALL_PERMISSIONS);
                  } else if (newRole === 'client') {
                    setSelectedGroupIds([6]);
                    setGranularPermissions(ROLE_PERMISSIONS.client);
                  } else if (newRole === 'driver') {
                    setSelectedGroupIds([7]);
                    setGranularPermissions(ROLE_PERMISSIONS.driver);
                  } else if (newRole === 'manager') {
                    setSelectedGroupIds([2, 3, 4]);
                    setGranularPermissions(ROLE_PERMISSIONS.manager);
                  } else if (newRole === 'user') {
                    setSelectedGroupIds([5]);
                    setGranularPermissions(ROLE_PERMISSIONS.user);
                  }
                }}
                className="mt-0.5 w-full bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-white text-xs outline-none cursor-pointer"
              >
                <option value="admin">Administrador (Acesso Total)</option>
                <option value="manager">Gerente / Operador</option>
                <option value="client">Cliente Obra (Área do Cliente)</option>
                <option value="driver">Motorista / Agregado (App PWA)</option>
                <option value="user">Usuário Padrão</option>
              </select>
            </div>

            <div className="min-w-0">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status do Usuário</div>
              <button
                type="button"
                onClick={() => setIsActive(!isActive)}
                className={`mt-0.5 w-full px-2.5 py-1 rounded-lg border font-bold flex items-center justify-between transition cursor-pointer ${
                  isActive
                    ? 'bg-emerald-950/40 border-emerald-700/60 text-emerald-300'
                    : 'bg-rose-950/40 border-rose-700/60 text-rose-300'
                }`}
              >
                <span>{isActive ? 'Ativo (Permitido)' : 'Inativo (Bloqueado)'}</span>
                <span className={`w-2 h-2 rounded-full shrink-0 ${isActive ? 'bg-emerald-400' : 'bg-rose-400'}`} />
              </button>
            </div>

            <div className="min-w-0">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Fonte das Permissões</div>
              <button
                type="button"
                onClick={handleToggleModoCustomizado}
                title={
                  usarPermissoesCustomizadas
                    ? 'Este usuário tem uma lista fixa própria - mudanças futuras nos grupos NÃO afetam ele. Clique para voltar a seguir o grupo.'
                    : 'Segue automaticamente o(s) grupo(s) selecionados na aba "Grupos Vinculados". Clique para fixar uma lista própria para este usuário.'
                }
                className={`mt-0.5 w-full px-2.5 py-1 rounded-lg border font-bold flex items-center justify-between transition cursor-pointer ${
                  usarPermissoesCustomizadas
                    ? 'bg-amber-950/40 border-amber-700/60 text-amber-300'
                    : 'bg-indigo-950/40 border-indigo-700/60 text-indigo-300'
                }`}
              >
                <span>{usarPermissoesCustomizadas ? 'Customizada (fixa)' : 'Padrão do Grupo'}</span>
                <span className={`w-2 h-2 rounded-full shrink-0 ${usarPermissoesCustomizadas ? 'bg-amber-400' : 'bg-indigo-400'}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Abas de Navegação */}
        <div className="px-4 sm:px-6 pt-3 flex gap-1.5 sm:gap-2 border-b border-slate-800 overflow-x-auto shrink-0 pb-1 scrollbar-none">
          <button
            onClick={() => setActiveTab('grupos')}
            className={`py-2 px-2.5 sm:px-3 text-xs font-bold border-b-2 transition flex items-center gap-1.5 whitespace-nowrap shrink-0 cursor-pointer ${
              activeTab === 'grupos'
                ? 'border-indigo-500 text-indigo-400 bg-indigo-950/20 rounded-t-lg'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            <span>Grupos Vinculados ({selectedGroupIds.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('granular')}
            className={`py-2 px-2.5 sm:px-3 text-xs font-bold border-b-2 transition flex items-center gap-1.5 whitespace-nowrap shrink-0 cursor-pointer ${
              activeTab === 'granular'
                ? 'border-indigo-500 text-indigo-400 bg-indigo-950/20 rounded-t-lg'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            <Sliders className="w-3.5 h-3.5" />
            <span>Permissões Granulares & Campos ({permissoesExibidas.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('programas_diretos')}
            className={`py-2 px-2.5 sm:px-3 text-xs font-bold border-b-2 transition flex items-center gap-1.5 whitespace-nowrap shrink-0 cursor-pointer ${
              activeTab === 'programas_diretos'
                ? 'border-indigo-500 text-indigo-400 bg-indigo-950/20 rounded-t-lg'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            <Layers className="w-3.5 h-3.5" />
            <span>Programas Diretos ({selectedDirectProgramIds.length})</span>
          </button>

          <button
            onClick={() => setActiveTab('visao_geral')}
            className={`py-2 px-2.5 sm:px-3 text-xs font-bold border-b-2 transition flex items-center gap-1.5 whitespace-nowrap shrink-0 cursor-pointer ${
              activeTab === 'visao_geral'
                ? 'border-indigo-500 text-indigo-400 bg-indigo-950/20 rounded-t-lg'
                : 'border-transparent text-slate-400 hover:text-white'
            }`}
          >
            <Shield className="w-3.5 h-3.5" />
            <span>Visão Geral Efetiva ({effectivePrograms.length} telas)</span>
          </button>
        </div>

        {/* Conteúdo da Aba */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-4 sm:space-y-6 flex-1 min-h-0">
          
          {/* Aba 1: Grupos */}
          {activeTab === 'grupos' && (
            <div className="space-y-3">
              <div className="text-xs text-slate-400">
                Selecione os grupos de segurança do Supabase que este usuário pertence (tabela <code className="text-indigo-300">system_user_group</code>).
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {availableGroups.map((grp) => {
                  const isSelected = selectedGroupIds.includes(grp.id);
                  return (
                    <div
                      key={grp.id}
                      onClick={() => handleToggleGroup(grp.id)}
                      className={`p-3.5 rounded-2xl border cursor-pointer transition flex items-start justify-between min-w-0 ${
                        isSelected
                          ? 'bg-indigo-950/40 border-indigo-500 text-white shadow-sm'
                          : 'bg-slate-950/60 border-slate-800 text-slate-400 hover:border-slate-700'
                      }`}
                    >
                      <div className="min-w-0 pr-2">
                        <div className="font-bold text-xs flex items-center gap-2">
                          <span className="truncate">{grp.name}</span>
                          <span className="text-[10px] text-slate-500 font-normal shrink-0">#{grp.id}</span>
                        </div>
                        <div className="text-[11px] text-slate-400 mt-1 truncate">
                          {grp.program_ids.length} programas vinculados em `system_group_program`
                        </div>
                      </div>

                      <div className={`w-5 h-5 rounded-lg border flex items-center justify-center shrink-0 ${
                        isSelected ? 'bg-indigo-600 border-indigo-500 text-white' : 'border-slate-700 bg-slate-900'
                      }`}>
                        {isSelected && <Check className="w-3.5 h-3.5" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Aba 2: Permissões Granulares & Campos Específicos */}
          {activeTab === 'granular' && (
            <div className="space-y-4">
              {!usarPermissoesCustomizadas && (
                <div className="p-3 rounded-2xl bg-indigo-950/40 border border-indigo-700/60 text-indigo-200 text-xs flex items-center justify-between gap-3">
                  <span>
                    <strong>Seguindo o(s) grupo(s)</strong> — esta lista é só leitura e reflete o que o grupo concede
                    agora. Para editar aqui, mude "Fonte das Permissões" para <strong>Customizada</strong> acima
                    (ou clique em qualquer opção abaixo).
                  </span>
                </div>
              )}

              <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-2.5 sm:gap-3 bg-slate-950/60 p-3 rounded-2xl border border-slate-800">
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 flex-1 min-w-0">
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1.5 text-white text-xs outline-none cursor-pointer w-full sm:w-auto min-w-[180px]"
                  >
                    <option value="todas">Todas as Categorias ({PERMISSION_DEFINITIONS.length})</option>
                    {categories.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>

                  <input
                    type="text"
                    value={searchPerm}
                    onChange={(e) => setSearchPerm(e.target.value)}
                    placeholder="Filtrar por nome ou ação..."
                    className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-1.5 text-white text-xs placeholder-slate-500 outline-none w-full sm:w-56 flex-1"
                  />
                </div>

                <div className="flex items-center gap-2 justify-end shrink-0">
                  <button
                    type="button"
                    onClick={handleClearSensitivePerms}
                    title="Oculta repasses, margens de lucro, exclusão e dados bancários para proteger a confidencialidade"
                    className="px-2.5 py-1.5 rounded-lg bg-amber-950/60 hover:bg-amber-900/60 border border-amber-800 text-amber-300 text-[11px] font-bold flex items-center gap-1.5 transition cursor-pointer flex-1 sm:flex-none justify-center"
                  >
                    <EyeOff className="w-3.5 h-3.5 shrink-0" />
                    <span>Ocultar Confidenciais</span>
                  </button>

                  <button
                    type="button"
                    onClick={handleSelectAllPerms}
                    className="px-2.5 py-1.5 rounded-lg bg-indigo-950/60 hover:bg-indigo-900/60 border border-indigo-800 text-indigo-300 text-[11px] font-bold flex items-center gap-1.5 transition cursor-pointer flex-1 sm:flex-none justify-center"
                  >
                    <Check className="w-3.5 h-3.5 shrink-0" />
                    <span>Marcar Todas</span>
                  </button>
                </div>
              </div>

              {/* Lista de Permissões com Checkboxes Individuais */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2.5">
                {filteredPermDefinitions.map((perm) => {
                  const isChecked = permissoesExibidas.includes(perm.id);

                  return (
                    <div
                      key={perm.id}
                      onClick={() => {
                        // Clicar num item enquanto segue o grupo já liga o modo
                        // customizado, partindo do que o grupo concede agora.
                        if (!usarPermissoesCustomizadas) {
                          const base = isChecked
                            ? permissoesDoGrupo.filter(p => p !== perm.id)
                            : [...permissoesDoGrupo, perm.id];
                          setGranularPermissions(base);
                          setUsarPermissoesCustomizadas(true);
                        } else {
                          handleToggleGranularPermission(perm.id);
                        }
                      }}
                      className={`p-3 rounded-xl border cursor-pointer transition flex items-start justify-between min-w-0 ${
                        isChecked
                          ? perm.isSensitive
                            ? 'bg-amber-950/30 border-amber-600/50 text-white'
                            : 'bg-indigo-950/40 border-indigo-500 text-white'
                          : 'bg-slate-950/40 border-slate-800 text-slate-400 hover:border-slate-700'
                      }`}
                    >
                      <div className="pr-2 min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="font-bold text-xs text-white">{perm.label}</span>
                          {perm.isSensitive && (
                            <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider bg-amber-950 text-amber-400 border border-amber-800 flex items-center gap-0.5 shrink-0">
                              <Lock className="w-2.5 h-2.5 shrink-0" />
                              Sensível
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5 leading-snug break-words">{perm.description}</div>
                        <div className="text-[9px] font-mono text-indigo-400/70 mt-1 truncate">{perm.id} • {perm.category}</div>
                      </div>

                      <div className={`w-5 h-5 rounded-lg border flex items-center justify-center shrink-0 ml-2 mt-0.5 ${
                        isChecked
                          ? perm.isSensitive ? 'bg-amber-600 border-amber-500 text-white' : 'bg-indigo-600 border-indigo-500 text-white'
                          : 'border-slate-700 bg-slate-900'
                      }`}>
                        {isChecked && <Check className="w-3.5 h-3.5" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Aba 3: Programas Diretos */}
          {activeTab === 'programas_diretos' && (
            <div className="space-y-3">
              <div className="text-xs text-slate-400">
                Atribua programas individualmente para este usuário além dos herdados pelo grupo (tabela <code className="text-indigo-300">system_user_program</code>).
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {allPrograms.map((prog) => {
                  const isDirect = selectedDirectProgramIds.includes(prog.id);
                  const isInherited = inheritedProgramIds.has(prog.id);

                  return (
                    <div
                      key={prog.id}
                      onClick={() => handleToggleDirectProgram(prog.id)}
                      className={`p-3 rounded-xl border cursor-pointer transition flex items-start justify-between min-w-0 ${
                        isDirect
                          ? 'bg-indigo-950/50 border-indigo-500 text-white'
                          : isInherited
                          ? 'bg-slate-950/80 border-slate-800 text-slate-300'
                          : 'bg-slate-950/40 border-slate-800/80 text-slate-400 hover:border-slate-700'
                      }`}
                    >
                      <div className="min-w-0 pr-2">
                        <div className="font-bold text-xs text-white truncate">{prog.name}</div>
                        <div className="text-[10px] font-mono text-indigo-400/90 mt-0.5 truncate">{prog.controller}</div>
                        {isInherited && (
                          <span className="inline-block mt-1 text-[9px] uppercase font-bold text-emerald-400 bg-emerald-950/80 border border-emerald-800 px-1.5 py-0.5 rounded">
                            Herdado de Grupo
                          </span>
                        )}
                      </div>

                      <div className={`w-5 h-5 rounded-lg border flex items-center justify-center shrink-0 ml-2 ${
                        isDirect ? 'bg-indigo-600 border-indigo-500 text-white' : 'border-slate-700 bg-slate-900'
                      }`}>
                        {isDirect && <Check className="w-3.5 h-3.5" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Aba 4: Visão Geral Efetiva */}
          {activeTab === 'visao_geral' && (
            <div className="space-y-4">
              <div className="text-xs text-slate-400">
                Resumo de todos os módulos que <strong>{user.name}</strong> pode acessar no sistema neste momento:
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {effectivePrograms.map((prog) => (
                  <div
                    key={prog.id}
                    className="p-2.5 rounded-xl bg-slate-950/70 border border-slate-800 flex items-center justify-between min-w-0"
                  >
                    <div className="min-w-0 pr-2">
                      <div className="text-xs font-bold text-white truncate">{prog.name}</div>
                      <div className="text-[10px] font-mono text-slate-400 truncate">{prog.controller}</div>
                    </div>
                    <span className="text-[10px] uppercase font-bold text-emerald-400 bg-emerald-950 border border-emerald-800 px-2 py-0.5 rounded shrink-0">
                      Liberado
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>

        {/* Footer */}
        <div className="p-3.5 sm:p-5 md:p-6 border-t border-slate-800 bg-slate-950 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-slate-400 flex items-center justify-between sm:justify-start gap-2">
            <span>{effectivePrograms.length} telas liberadas</span>
            <span>•</span>
            <span className="text-indigo-400 font-bold">{granularPermissions.length} permissões ativas</span>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 sm:flex-none px-4 py-2.5 rounded-xl border border-slate-700 hover:bg-slate-800 text-slate-300 text-xs font-bold transition cursor-pointer text-center"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleSalvar}
              disabled={salvando}
              className="flex-1 sm:flex-none px-5 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold transition flex items-center justify-center gap-2 shadow-lg shadow-indigo-600/30 disabled:opacity-50 cursor-pointer"
            >
              <Save className="w-4 h-4 shrink-0" />
              <span>{salvando ? 'Salvando...' : 'Salvar no Supabase'}</span>
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

